/**
 * PremiumServicesEditor · v2.7.65+E237
 * ====================================
 * Website-Management editor for the public home-page "Premium Services &
 * Add-Ons" section. Fully AUTOSAVING — every change debounces a PUT to
 * /admin/site/premium-services, so there is NO save button. Photos/icons
 * upload (≤25 MB) via /admin/uploads/premium (GridFS).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "@/components/admin/ImageUploader";
import adminApi from "@/lib/adminApi";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Check, CloudOff, Sparkles, Plane, Luggage, UserCheck,
  Gem, Crown, Gift, Wifi, Baby, Dog, Snowflake, Star, ShieldCheck, Clock, Car,
  Briefcase, DollarSign, Tag,
} from "lucide-react";

const PREMIUM_ICONS = {
  Sparkles, Plane, Luggage, UserCheck, Gem, Crown, Gift, Wifi, Baby, Dog,
  Snowflake, Star, ShieldCheck, Clock, Car, Briefcase,
};
const ICON_NAMES = Object.keys(PREMIUM_ICONS);
const MAX_25 = 25 * 1024 * 1024;

const newItem = () => ({
  id: `psi_${Math.random().toString(36).slice(2, 10)}`,
  name: "", description: "", image_url: "", icon: "Sparkles",
  price_mode: "fixed", price: "", price_unit: "", enabled: true,
});

export default function PremiumServicesEditor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const timer = useRef(null);

  useEffect(() => {
    api.get("/site/premium-services")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load Premium Services"))
      .finally(() => setLoading(false));
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, []);

  const scheduleSave = useCallback((next) => {
    setSaveState("saving");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await adminApi.put("/admin/site/premium-services", next);
        setSaveState("saved");
      } catch {
        setSaveState("error");
        toast.error("Autosave failed — retrying on next change");
      }
    }, 700);
  }, []);

  // Immutable update + debounced autosave.
  const update = useCallback((fn) => {
    setData((cur) => {
      const next = JSON.parse(JSON.stringify(cur));
      fn(next);
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  if (loading || !data) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500" data-testid="premium-editor-loading">
        <Loader2 className="h-7 w-7 animate-spin mx-auto" />
      </Card>
    );
  }

  const items = data.items || [];

  return (
    <div className="space-y-4 mt-3" data-testid="premium-editor">
      {/* Header + autosave status */}
      <Card className="bg-zinc-900 border-amber-400/30 p-5" data-testid="premium-editor-header">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-black flex items-center gap-2 text-zinc-100">
              <Sparkles className="h-5 w-5 text-amber-400" />Premium Services &amp; Add-Ons
            </h3>
            <p className="text-xs text-zinc-400 mt-1">
              Shown on the home page directly above Taxi Rates. Every change saves automatically — no save button.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <SaveBadge state={saveState} />
            <label className="flex items-center gap-2 text-xs font-bold text-zinc-300" data-testid="premium-enabled-toggle">
              <Switch checked={!!data.enabled} onCheckedChange={(v) => update((d) => { d.enabled = v; })} />
              {data.enabled ? "Section visible" : "Section hidden"}
            </label>
          </div>
        </div>
      </Card>

      {/* Section settings */}
      <Card className="bg-zinc-950 border border-zinc-800 p-4 space-y-4" data-testid="premium-section-settings">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Labeled label="Section heading">
            <Input data-testid="premium-heading" value={data.heading || ""}
              onChange={(e) => update((d) => { d.heading = e.target.value; })}
              className="bg-zinc-900 border-zinc-700 text-zinc-100" />
          </Labeled>
          <Labeled label="Subheading (optional)">
            <Input data-testid="premium-subheading" value={data.subheading || ""}
              onChange={(e) => update((d) => { d.subheading = e.target.value; })}
              className="bg-zinc-900 border-zinc-700 text-zinc-100" />
          </Labeled>
        </div>

        {/* Background: color OR image */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Section background</div>
          <div className="flex items-center gap-2 bg-zinc-900 rounded-md p-1 border border-zinc-800 w-fit" data-testid="premium-bg-mode">
            {["color", "image"].map((m) => (
              <button key={m} type="button" data-testid={`premium-bg-mode-${m}`}
                onClick={() => update((d) => { d.bg_mode = m; })}
                className={`px-3 py-1 rounded text-xs font-bold capitalize transition-colors ${data.bg_mode === m ? "bg-amber-400 text-zinc-950" : "text-zinc-400 hover:text-zinc-200"}`}>
                {m}
              </button>
            ))}
          </div>
          {data.bg_mode === "image" ? (
            <ImageUploader
              testid="premium-bg"
              label="Background image (≤25 MB)"
              value={data.bg_image_url}
              onChange={(url) => update((d) => { d.bg_image_url = url; })}
              endpoint="/admin/uploads/premium"
              maxBytes={MAX_25}
              height={140}
            />
          ) : (
            <div className="flex items-center gap-3" data-testid="premium-bg-color">
              <input type="color" value={data.bg_color || "#0b0b0c"}
                onChange={(e) => update((d) => { d.bg_color = e.target.value; })}
                className="h-10 w-16 rounded cursor-pointer bg-transparent border border-zinc-700"
                data-testid="premium-bg-color-picker" />
              <Input value={data.bg_color || ""} onChange={(e) => update((d) => { d.bg_color = e.target.value; })}
                placeholder="#0b0b0c" className="bg-zinc-900 border-zinc-700 text-zinc-100 w-40 font-mono" />
            </div>
          )}
        </div>
      </Card>

      {/* Items — displayed in groups of three */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="premium-items">
        {items.map((it, idx) => (
          <ItemCard key={it.id} it={it} idx={idx} update={update} />
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center text-zinc-500 text-sm py-8 border border-dashed border-zinc-800 rounded-lg" data-testid="premium-empty">
            No services yet — add your first group of three below.
          </div>
        )}
      </div>

      <Button data-testid="premium-add-three" variant="outline"
        className="bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
        onClick={() => update((d) => { d.items = [...(d.items || []), newItem(), newItem(), newItem()]; })}>
        <Plus className="h-4 w-4 mr-1.5" />Add 3 Services
      </Button>
    </div>
  );
}

function ItemCard({ it, idx, update }) {
  const setField = (k, v) => update((d) => { d.items[idx][k] = v; });
  const Icon = PREMIUM_ICONS[it.icon] || Sparkles;
  return (
    <Card className="bg-zinc-950 border border-zinc-800 p-3 space-y-3" data-testid={`premium-item-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Service #{idx + 1}</span>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[10px] text-zinc-400" data-testid={`premium-item-${idx}-enabled`}>
            <Switch checked={!!it.enabled} onCheckedChange={(v) => setField("enabled", v)} />
            {it.enabled ? "Live" : "Hidden"}
          </label>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-500 hover:text-rose-400"
            data-testid={`premium-item-${idx}-delete`}
            onClick={() => update((d) => { d.items.splice(idx, 1); })}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ImageUploader
        testid={`premium-item-${idx}-photo`}
        label="Photo (≤25 MB)"
        value={it.image_url}
        onChange={(url) => setField("image_url", url)}
        endpoint="/admin/uploads/premium"
        maxBytes={MAX_25}
        height={120}
      />

      {/* Icon fallback (used when no photo is set) */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5 flex items-center gap-1">
          <Icon className="h-3.5 w-3.5 text-amber-400" />Fallback icon (if no photo)
        </div>
        <div className="flex flex-wrap gap-1" data-testid={`premium-item-${idx}-icons`}>
          {ICON_NAMES.map((nm) => {
            const IC = PREMIUM_ICONS[nm];
            return (
              <button key={nm} type="button" title={nm}
                data-testid={`premium-item-${idx}-icon-${nm}`}
                onClick={() => setField("icon", nm)}
                className={`h-7 w-7 rounded flex items-center justify-center border transition-colors ${it.icon === nm ? "border-amber-400 bg-amber-400/15 text-amber-300" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
                <IC className="h-4 w-4" />
              </button>
            );
          })}
        </div>
      </div>

      <Input data-testid={`premium-item-${idx}-name`} value={it.name || ""}
        onChange={(e) => setField("name", e.target.value)} placeholder="Service name"
        className="bg-zinc-900 border-zinc-700 text-zinc-100 font-bold" />

      <textarea data-testid={`premium-item-${idx}-desc`} value={it.description || ""}
        onChange={(e) => setField("description", e.target.value)} rows={2}
        placeholder="Feature description"
        className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 resize-y" />

      {/* Price mode: fixed OR per feature */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 bg-zinc-900 rounded-md p-1 border border-zinc-800" data-testid={`premium-item-${idx}-price-mode`}>
          {[["fixed", "Fixed price"], ["per_feature", "Per feature"]].map(([m, lbl]) => (
            <button key={m} type="button" data-testid={`premium-item-${idx}-price-mode-${m}`}
              onClick={() => setField("price_mode", m)}
              className={`flex-1 px-2 py-1 rounded text-[11px] font-bold transition-colors ${it.price_mode === m ? "bg-amber-400 text-zinc-950" : "text-zinc-400 hover:text-zinc-200"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
            <Input data-testid={`premium-item-${idx}-price`} value={it.price || ""}
              onChange={(e) => setField("price", e.target.value)} placeholder="0.00" inputMode="decimal"
              className="bg-zinc-900 border-zinc-700 text-zinc-100 pl-7" />
          </div>
          {it.price_mode === "per_feature" && (
            <div className="relative flex-1">
              <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <Input data-testid={`premium-item-${idx}-unit`} value={it.price_unit || ""}
                onChange={(e) => setField("price_unit", e.target.value)} placeholder="per bag / per stop"
                className="bg-zinc-900 border-zinc-700 text-zinc-100 pl-7" />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function SaveBadge({ state }) {
  const map = {
    idle:   { cls: "text-zinc-500", node: <>Autosaves</> },
    saving: { cls: "text-amber-300", node: <><Loader2 className="h-3 w-3 animate-spin" />Saving…</> },
    saved:  { cls: "text-emerald-400", node: <><Check className="h-3 w-3" />Saved</> },
    error:  { cls: "text-rose-400", node: <><CloudOff className="h-3 w-3" />Save failed</> },
  };
  const s = map[state] || map.idle;
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 ${s.cls}`} data-testid="premium-save-state">
      {s.node}
    </span>
  );
}

function Labeled({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">{label}</div>
      {children}
    </label>
  );
}
